import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Clock, CheckCircle, XCircle, Users, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WorkerRapportiniList() {
  const [rapportini, setRapportini] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchRapportini = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/rapportini`, { withCredentials: true });
      setRapportini(response.data);
    } catch (error) {
      console.error('Error fetching rapportini:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRapportini();
  }, [fetchRapportini]);

  const statusConfig = {
    in_attesa: { label: 'In Attesa', icon: Clock, color: 'bg-amber-100 text-amber-800 border-amber-200' },
    approvato: { label: 'Approvato', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    rifiutato: { label: 'Rifiutato', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200' }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4" data-testid="worker-rapportini-list">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">I Miei Rapportini</h1>
        <Button
          onClick={() => navigate('/worker/rapportini/nuovo')}
          className="bg-slate-900 text-white hover:bg-slate-800"
          data-testid="create-rapportino-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuovo
        </Button>
      </div>

      {rapportini.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Clock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Nessun rapportino inserito</p>
            <Button
              onClick={() => navigate('/worker/rapportini/nuovo')}
              className="mt-4 bg-slate-900 text-white hover:bg-slate-800"
            >
              Crea il tuo primo rapportino
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rapportini.map((rapportino) => {
            const status = statusConfig[rapportino.status] || statusConfig.in_attesa;
            const StatusIcon = status.icon;
            const isCited = rapportino.is_cited;
            // Build squadra list (workers + colleghi), excluding the current viewing user
            const myId = user?.id;
            const squadraNames = [];
            const seen = new Set();
            (rapportino.workers_list || []).forEach(w => {
              if (!w?.name) return;
              if (w.worker_id && w.worker_id === myId) return;
              const key = (w.worker_id || w.name).toLowerCase();
              if (seen.has(key)) return;
              seen.add(key);
              squadraNames.push(w.name);
            });
            (rapportino.colleghi || []).forEach(c => {
              if (!c?.name) return;
              if (c.id && c.id === myId) return;
              const key = (c.id || c.name).toLowerCase();
              if (seen.has(key)) return;
              seen.add(key);
              squadraNames.push(c.name);
            });
            return (
              <Card key={rapportino.id} className={`border-slate-200 ${isCited ? 'border-l-4 border-l-blue-400' : ''}`} data-testid={`rapportino-card-${rapportino.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {rapportino.numero && (
                          <span className="font-mono text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">#{rapportino.numero}</span>
                        )}
                        <span className="font-medium text-slate-900">
                          {format(new Date(rapportino.date), 'dd MMMM yyyy', { locale: it })}
                        </span>
                        <Badge variant="outline" className={status.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.label}
                        </Badge>
                        {isCited && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            Citato da {rapportino.worker_name}
                          </Badge>
                        )}
                      </div>
                      {squadraNames.length > 0 && (
                        <div className="mt-2 flex items-start gap-1.5 p-2 bg-blue-50 border border-blue-100 rounded" data-testid={`squadra-${rapportino.id}`}>
                          <Users className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Colleghi presenti</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {squadraNames.map((name) => (
                                <span key={name} className="inline-block text-xs font-medium text-blue-800 bg-white border border-blue-200 px-1.5 py-0.5 rounded">
                                  {name}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      <p className="text-sm text-slate-500 line-clamp-2 mt-2">{rapportino.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span>
                          {rapportino.hours_worked}h
                          {rapportino.ora_inizio && rapportino.ora_fine && (
                            <> ({rapportino.ora_inizio}-{rapportino.ora_fine})</>
                          )}
                        </span>
                        {rapportino.ore_straordinarie > 0 && (
                          <span className="text-red-500 font-medium">{rapportino.ore_straordinarie}h straord.</span>
                        )}
                        {rapportino.trasferta && (
                          <span className="flex items-center gap-0.5 text-orange-500 font-medium">
                            <MapPin className="h-3 w-3" />Trasferta
                          </span>
                        )}
                        {rapportino.materials_used?.length > 0 && (
                          <span>{rapportino.materials_used.length} materiali</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {rapportino.admin_notes && rapportino.status !== 'in_attesa' && (
                    <div className="mt-3 p-2 bg-slate-50 rounded border border-slate-200">
                      <p className="text-xs text-slate-500">Note admin: {rapportino.admin_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
